import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';

import { ButtonBars } from './Loader';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

/**
 * Filled buttons carry dark labels, not white.
 *
 * Signal is a light indigo and white on it fails contrast badly. The mockups
 * agree - every filled control in the design has a near-black label.
 */
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent text-accent-ink hover:bg-accent-hover',
  secondary: 'bg-transparent text-ink border-line-strong hover:border-line-lit hover:bg-raised',
  ghost: 'text-soft hover:text-ink hover:bg-raised',
  /*
   * Hot, used sparingly.
   *
   * In this design Hot means "a room is live", so a destructive button
   * borrowing it dilutes the one association the interface leans on hardest.
   * Reserved for genuinely irreversible actions, and there should be very few.
   */
  danger: 'bg-hot text-white hover:opacity-90',
};

/*
 * Pill by default: the design's CTAs are all fully rounded.
 *
 * The transparent border is load-bearing, not tidying. `secondary` draws one
 * and the others do not, so without this a filled button is 40px and an
 * outlined one beside it is 42 - a difference small enough to survive review
 * and large enough to see in any row that mixes them. Declaring it here and
 * letting the variants recolour it makes every button the same height by
 * construction.
 */
const BASE = [
  'inline-flex items-center justify-center gap-2 rounded-[var(--radius-pill)]',
  'border border-transparent px-5 py-2.5 text-sm font-medium transition-colors',
  'disabled:cursor-not-allowed disabled:opacity-50',
].join(' ');

function classes(variant: Variant, full: boolean, extra: string): string {
  return [BASE, VARIANTS[variant], full ? 'w-full' : '', extra].filter(Boolean).join(' ');
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  loading?: boolean;
  full?: boolean;
  children: ReactNode;
}

export function Button({
  variant = 'primary',
  loading = false,
  full = false,
  disabled,
  children,
  className = '',
  ...rest
}: ButtonProps) {
  return (
    <button
      // A button that is busy must also be inert. Leaving it clickable during a
      // request is how you get two accounts from one impatient double-click.
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      // Not `submit` by default: an unmarked button inside a form submits it,
      // which silently turns every "cancel" into a submit.
      type={rest.type ?? 'button'}
      className={classes(variant, full, className)}
      {...rest}
    >
      {loading && <ButtonBars />}
      {children}
    </button>
  );
}

/**
 * A link that looks like a button.
 *
 * Exists because `<Link><Button/></Link>` - the obvious thing to write - nests a
 * `<button>` inside an `<a>`. That is invalid HTML, and more concretely it puts
 * two overlapping interactive elements in the accessibility tree, so a screen
 * reader announces the control twice and keyboard users tab through it twice.
 *
 * "Navigate somewhere" and "do something" are different controls and the
 * element should say which it is.
 */
interface LinkButtonProps extends Omit<LinkProps, 'className'> {
  variant?: Variant;
  full?: boolean;
  className?: string;
  children: ReactNode;
}

export function LinkButton({ variant = 'primary', full = false, className = '', children, ...rest }: LinkButtonProps) {
  return (
    <Link className={classes(variant, full, className)} {...rest}>
      {children}
    </Link>
  );
}
